import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  Progress,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import EtherPrice from "components/EtherPrice";
import IncentiveTile, { IncentiveTileProps } from "components/IncentiveTile";
import {
  bearHolderDiscount,
  reservedSalesCount,
  saleTiers,
} from "config/sales";
import useAssets from "hooks/useAssets";
import useTranslate from "hooks/useTranslate";
import useV2s from "hooks/useV2s";
import { FaCircle } from "react-icons/fa";
import { Currency } from "types";

type IncentivesContainerProps = StackProps & {};

const IncentivesContainer = (props: IncentivesContainerProps) => {
  const translate = useTranslate();
  const { allV2Ids } = useV2s();
  const { fancyBearsInWallet } = useAssets();

  const isBearHolder = Boolean(fancyBearsInWallet?.length);
  let totalSupply = allV2Ids.length || 0;

  if (reservedSalesCount && totalSupply < reservedSalesCount) {
    totalSupply = reservedSalesCount;
  }

  return (
    <VStack w="full" align="stretch" spacing="2px" {...props}>
      {saleTiers.map(({ maxSupply, minSupply, price, honeyReward }, index) => {
        const pheaseMin = Math.min(
          Math.max(totalSupply - (minSupply - 1), 0),
          maxSupply
        );
        const pheaseMax = maxSupply - (minSupply - 1);
        const progress = (pheaseMin / pheaseMax) * 100;

        const progressLabel = `${pheaseMin} / ${pheaseMax}`;
        const displayTitle = `${translate(
          `sales:title:${index + 1}`
        )}: ${minSupply} - ${maxSupply} sales`;

        let status: IncentiveTileProps["status"] = "active";
        if (totalSupply < maxSupply && totalSupply >= minSupply - 1) {
          status = "done";
        } else if (totalSupply >= maxSupply) {
          status = "inactive";
        }

        return (
          <Box key={index}>
            <IncentiveTile
              title={displayTitle}
              status={status}
              icon={
                status === "active" ? (
                  <Icon as={FaCircle} w="2" h="2" />
                ) : undefined
              }
              details={
                progress !== undefined ? (
                  <HStack align="center" mt={1}>
                    <Progress
                      value={progress}
                      h="2"
                      colorScheme="primary"
                      w="20"
                    />

                    {progressLabel && (
                      <Text fontSize="xs" opacity="0.6">
                        {progressLabel}
                      </Text>
                    )}
                  </HStack>
                ) : undefined
              }
            >
              <HStack spacing="0">
                <VStack spacing="0" align="flex-start" w="20">
                  <Text
                    opacity="0.6"
                    fontSize="0.625rem"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    {translate("common:reward")}
                  </Text>
                  <EtherPrice
                    opacity={status === "inactive" ? "0.6" : undefined}
                    currency={Currency.Honey}
                    amount={honeyReward}
                    iconColor="primary.500"
                    fontWeight="semibold"
                    fontSize="sm"
                    iconSize="4"
                  />
                </VStack>
                <VStack spacing="0" align="flex-start" w="12">
                  <Text
                    opacity="0.6"
                    fontSize="0.625rem"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    {translate("common:price")}
                  </Text>
                  <EtherPrice
                    opacity={status === "inactive" ? "0.6" : undefined}
                    currency={Currency.Eth}
                    amount={price}
                    asPrice
                    iconColor="primary.500"
                    fontWeight="semibold"
                    fontSize="sm"
                    iconSize="4"
                  />
                </VStack>
              </HStack>
            </IncentiveTile>
          </Box>
        );
      })}

      <IncentiveTile
        mt="3"
        status={isBearHolder ? "done" : "active"}
        icon={
          !isBearHolder ? <Icon as={FaCircle} w="2.5" h="2.5" /> : undefined
        }
        title={translate("incentives:bearHolder:title", {
          discount: bearHolderDiscount,
        })}
        details={
          <Text fontSize="xs" opacity="0.6" mt="0.5">
            {isBearHolder
              ? translate("incentives:bearHolder:description:done", {
                  discount: bearHolderDiscount,
                  amount: fancyBearsInWallet?.length,
                })
              : translate("incentives:bearHolder:description")}
          </Text>
        }
      >
        {isBearHolder ? (
          <VStack spacing="1" align="flex-start" mr="1">
            <Text
              opacity="0.6"
              fontSize="0.625rem"
              fontWeight="semibold"
              textTransform="uppercase"
            >
              {translate("common:discount")}
            </Text>
            <Badge bg="primary.500" color="dark.900" px="2">
              {translate("incentives:bearHolder:badge", {
                discount: bearHolderDiscount,
              })}
            </Badge>
          </VStack>
        ) : (
          <Button size="xs" variant="outline">
            {translate("common:buy")}
          </Button>
        )}
      </IncentiveTile>
    </VStack>
  );
};

export default IncentivesContainer;
